/**
 * Created by SH: 02/20/2020
 */
import {  gsap } from "gsap";
import { IReelConfig, IReelDim } from "./IData/IReelData";
import { ReelSpriteSymbol } from "./symbol/ReelSpriteSymbol";
import { ReelBaseSymbol } from "./symbol/ReelBaseSymbol";
import { GameSetting } from "../setting/GameSetting";
import { StateHandler } from "../utils/StateHandler";
import { ReelEngine } from "./engine/ReelEngine";
import { ReelSymbolStates } from "./symbol/ReelSpriteSymbolArea";
import { ArrayHelper } from "../utils/ArrayHelper";
import { Helper } from "../utils/Helper";

export class ReelState extends StateHandler {

    //* Basic Behaviours *//
    public static readonly START: string = "start";
    public static readonly STEP: string = "step";
    public static readonly STOP: string = "stop";
    public static readonly FAST_STOP: string = "fast_stop";
    public static readonly QUICK_STOP: string = "quick_stop";
    public static readonly IDLE: string = "idle";
    public static readonly INACTIVE: string = "inactive";

    //*  Created Behaviours *//
    public static readonly REVERSE: string = "reverse";
    public static readonly NUDGE: string = "nudge";

    // * To be created... * //
    public static readonly EXPAND_SYM: string = "expand_symbol";
    public static readonly STOP_TEASER: string = "stop_teaser";
    public static readonly START_TEASER: string = "start_teaser";

    public get isSpinning(): boolean {
        return this.equal(ReelState.STEP);
    };

    public get isStarting(): boolean {
        return this.equal(ReelState.START);
    };

    public get isIdle(): boolean {
        return this.equal(ReelState.IDLE);
    };

    public get isInactive(): boolean {
        return this.equal(ReelState.INACTIVE);
    };

    public get isNudging(): boolean {
        return this.equal(ReelState.NUDGE);
    };

    public get isStopping(): boolean {
        return this.equal(ReelState.STOP) || this.equal(ReelState.QUICK_STOP) || this.equal(ReelState.FAST_STOP);
    };
}


export class ReelEngineView extends PIXI.Container {

    private _id: number;
    private _config: IReelConfig;

    protected _engine: ReelEngine;
    protected _nextReelSetName: string = "NORMAL";
    protected _reelState: ReelState = new ReelState(ReelState.INACTIVE);

    private genSym: Generator<string, string, string>;

    protected _hasInit: boolean = false;
    protected _isQuickStop: boolean = false;

    protected _maskObject: PIXI.Graphics;

    protected _graphTl: gsap.core.Timeline;
    protected _graphArea: PIXI.Graphics;

    protected _activeReel: boolean = true;
    protected _activeForNextSpin: boolean = true;

    /* Saved ServerData. */
    protected _stopReelSet: string[]; //The symbols displayed when reel has stopped
    protected _currentReelSet: string[]; //The reelSet currently in use, it might be different than the actually reelSet because its made of many different parts.
    protected _quickStopReelSet: string[]; //The symbols displayed when reel has stopped
    /* ----------------  */
    protected _animationContainer: PIXI.Container = new PIXI.Container();
    protected _symbolContainer: PIXI.Container = new PIXI.Container();
    protected _stickyContainer: PIXI.Container = new PIXI.Container();
    // Used for calculating the static transformPositions on noneMoving reel. Feel free to refractor&optimize away this.

    public get allSymbolAmount(): number { return this.AllSymbols.reduce((a, b) => { return a + b.stackedValue}, 0); }

    public get currentWindowAmount(): number { return this.AllWindowSymbols.reduce((a, b) => { return a + b.stackedValue}, 0); }

    public get windowConfAmount(): number { return ArrayHelper.fetchArrTypeValue(this.config.WINDOW_AMOUNT,this.reelID) }

    public get windowConfHeight(): number { return ArrayHelper.fetchArrTypeValue(this.config.DIM.HEIGHT,this.reelID) }

    public get symbolConfAmount(): number { return this.windowConfAmount + this.overflowAmount*2; }

    public get amountOfStopSymbols(): number { return this.windowConfAmount + this.config.STOP_DISTANCE; }

    public get reelID(): number { return this._id; }

    public get engine(): ReelEngine { return this._engine; }

    public get state(): ReelState { return this._reelState; }

    public get isQuickStop(): boolean { return this._isQuickStop; }

    public set isQuickStop(value: boolean) { this._isQuickStop = value; }

    public get AllSymbols(): ReelBaseSymbol[] { return this._symbolContainer.children as ReelBaseSymbol[]; }

    public get AllStickySymbols(): ReelBaseSymbol[] { return this._stickyContainer.children as ReelBaseSymbol[]; }

    public set AllSymbols(symbols: ReelBaseSymbol[]) {
        this._symbolContainer.removeChildren();
        symbols.sort((a: ReelBaseSymbol, b: ReelBaseSymbol) => { return a.posID[0] - b.posID[0]; })
        symbols.forEach(sym => { this._symbolContainer.addChild(sym); });
    }

    public get topSymbol(): ReelBaseSymbol { return this.AllSymbols[0]; }
    public get bottomSymbol(): ReelBaseSymbol { return this.AllSymbols[this.AllSymbols.length - 1]; }

    public get windowTopSymbol(): ReelBaseSymbol { return this.AllSymbols[this.overflowAmount] }
    public get windowBottSymbol(): ReelBaseSymbol { return this.AllSymbols[this.AllSymbols.length - (this.overflowAmount*2)]; }

    public get AllWindowSymbols(): ReelBaseSymbol[] {
        return this.AllSymbols.filter(sym => { return (Math.max(...sym.posID) >= 0 && Math.min(...sym.posID) < (this.symbolConfAmount - (this.overflowAmount*2))) });
    }

    public get currentStepDistance(): number { 
        return this.windowConfHeight/this.windowConfAmount +  ArrayHelper.fetchArrTypeValue(this.config.DIM.PADDING_Y,this.reelID);
    }

    public get config(): IReelConfig { return this._config; }
    public get reelDim(): IReelDim { return this.config.DIM; }

    public get maxHeight(): number { return ArrayHelper.fetchArrTypeValue(this.reelDim.HEIGHT, this.reelID); }
    public get maxWidth(): number { return ArrayHelper.fetchArrTypeValue(this.reelDim.WIDTH, this.reelID); }

    public get overflowAmount(): number {
        return this.config.OVERFLOW || this.config.RESULT_OVERFLOW ? 1 : 0;
    }

    public get isActive(): boolean {
        return this._activeReel && this.visible;
    };

    public get statePosOffset(): number {
        return this.state.isIdle ? 0 : this.engine.reverse ? 1 : -1;
    }

    public set isActive(value: boolean) {
        this._activeReel = value;
    };

    public get isFast(): boolean {
        return this.isQuickStop || GameSetting.isFastSpin || this.state.equal(ReelState.FAST_STOP);
    };

    public get startDelay(): number {
        return GameSetting.isFastSpin ? this.config.DELAY.FAST_START : this.config.DELAY.START;
    }

    public get stopDelay(): number {
        // if (this.isQuickStop) return this.config.DELAY.QUICK_STOP;
        return GameSetting.isFastSpin ? this.config.DELAY.FAST_STOP : this.config.DELAY.STOP;
    }

    public get startDuration(): number {
        return GameSetting.isFastSpin ? this.config.DURATION.FAST_START : this.config.DURATION.START;
    }

    public get stopDuration(): number {
        // if (this.isQuickStop) return this.config.DURATION.QUICK_STOP;
        return GameSetting.isFastSpin ? this.config.DURATION.FAST_STOP : this.config.DURATION.STOP;
    }

    public get spinDuration(): number {
        return GameSetting.isFastSpin ? this.config.DURATION.FAST_SPIN : this.config.DURATION.SPIN;
    }

    public get startEase(): any {
        return GameSetting.isFastSpin ? this.config.EASING.FAST_START : this.config.EASING.START;
    }

    public get stopEase(): any {
        if (this.isQuickStop) return this.config.EASING.QUICK_STOP;
        return GameSetting.isFastSpin ? this.config.EASING.FAST_STOP : this.config.EASING.STOP;
    }

    constructor(id: number, config: IReelConfig) {
        super();
        this._id = id;
        this._config = Helper.assignObj({},config);
        this.addChildObjOrder();
        if (!this.genSym) this.genSym = this.getNextSym();
    }

    public symbolExcist(symbolID: number): boolean { return !!this.getSymbol(symbolID); }

    public getSymbol(symbolID: number): ReelBaseSymbol | undefined {
        return this.AllSymbols.find((sym: ReelBaseSymbol) => { return sym.posID.includes(symbolID) });
    }

    protected addChildObjOrder(): void {
        
        this.createMask();

        this._graphArea = this.createGraphic();
        this._graphArea.alpha = 0;
        this._graphArea.visible = false;

        this.addChild(this._animationContainer);
        this._animationContainer.addChild(this._symbolContainer);
        this.addChild(this._stickyContainer);
    }

    protected createMask(): void {
        if(this.config.INDIVIDUAL_MASK){
            this.mask = this._maskObject = this.createGraphic();
            this.mask.name = "mask";
        }
    }

    protected createGraphic(color:number = 0x000000, alpha:number = 1): PIXI.Graphics {
        const graphicBg: PIXI.Graphics = new PIXI.Graphics();
        graphicBg.beginFill(color, alpha);
        graphicBg.drawRect(-this.maxWidth * 0.5, -this.currentStepDistance *0.5,
            this.maxWidth , this.maxHeight);
        graphicBg.endFill();
        this.addChild(graphicBg);
        return graphicBg;
    }

    protected createNewSymbol(id: number, symbolName: string, state?: string, type?: number): ReelBaseSymbol { 
        return new ReelSpriteSymbol(id, symbolName,state,type ? type : this.windowConfAmount); 
    }

    protected createRandomSymbol(): ReelBaseSymbol {
        return this.createNewSymbol(this.children.length, this.generateNextSymbol());
    }

    protected addSymbol(symbol: PIXI.DisplayObject): void {
        this._symbolContainer.addChild(symbol);
        this.AllSymbols.sort((a: ReelBaseSymbol, b: ReelBaseSymbol) => { return a.posID[0] - b.posID[0]; });
    }

    protected removeItemPos(removePos: number[]): void {
        let find: boolean = true;

        do{
            find = false;
            for (let i: number = 0; i < this.AllSymbols.length; i++) {
                for(let j: number = 0; j < removePos.length; j++){
                    if(this.AllSymbols[i].posID.indexOf(removePos[j]) >= 0){
                        this._symbolContainer.removeChildAt(i);
                        find = true;
                        break;
                    }
                }
            }
        }
        while (find)
    }

    protected addingNextStepSymbol(_posID: number, _sym: string, _state: string): ReelBaseSymbol {
        return this.createNewSymbol(_posID, _sym, _state);
    }

    protected setVisibleForOverflowSymbols(value: boolean): void {
        if (!this.isActive) return;
        if(this._maskObject){
            this.mask = value ? this._maskObject : undefined!;
            this._maskObject.alpha = this.mask ? 1 : 0;
        }

        for (let indx: number = 0; indx < this.AllSymbols.length; indx++) {
            const posID: number[] = this.AllSymbols[indx].posID;
            if (Math.max(...posID) < 0 || Math.min(...posID) >= this.windowConfAmount) this.AllSymbols[indx].setVisible(value);

            if(this._maskObject){
                if(this.AllSymbols[indx].visible && (Math.min(...posID) < 0 || Math.max(...posID) >= this.windowConfAmount) ){
                    this.mask = this._maskObject;
                    this._maskObject.alpha = 1;
                }
            }
        }
    }

    public addAndRemoveOutOfBound() {
        while (this.tryRemoveSymbol(this.engine.reverse));
        while (this.tryAddNextSymbol(this.engine.reverse));
    }

    protected removeAllSymbols() {
        this._symbolContainer.removeChildren().forEach((c) => (c as ReelBaseSymbol).remove());
        this._stickyContainer.removeChildren().forEach((c) => (c as ReelBaseSymbol).remove());
    }

    protected checkSymbolBoundries(symbolToCheck: ReelBaseSymbol, inside: boolean, reverse: boolean): boolean {

        let posY: number = symbolToCheck.position.y + ((reverse ? 1 : -1) * (symbolToCheck.symbolHeight));

        const engineStep: number = Math.abs(this.engine.stepDistance);
        const sizeMatter:number = symbolToCheck.symbolHeight;
        const overflowOffset: number = sizeMatter * this.overflowAmount;

        const fromDirection: number = overflowOffset;
        const toDirection: number = (this.maxHeight - engineStep) + overflowOffset;

        // Check if it is visible by Restriction and symbol size.
        let fromTop: boolean = false;
        let fromBott: boolean = false;

        if (inside) {
            fromTop = (!reverse && posY >= -fromDirection)
            fromBott = (reverse && posY <= toDirection);
        } else {
            fromTop = (reverse && posY <= -fromDirection)
            fromBott = (!reverse && posY >= toDirection);
            if (fromTop || fromBott) (fromTop ? this.AllSymbols.shift() : this.AllSymbols.pop())!.remove();
        }

        return fromTop || fromBott;
    }

    protected tryRemoveSymbol(reverse: boolean): boolean {
        const symbolToCheck: ReelBaseSymbol = reverse ? this.topSymbol : this.bottomSymbol;
        return this.checkSymbolBoundries(symbolToCheck, false, reverse);
    }

    protected tryAddNextSymbol(reverse: boolean): boolean {

        const symbolToCheck: ReelBaseSymbol = reverse ? this.bottomSymbol : this.topSymbol;
        const willSpawn: boolean = this.checkSymbolBoundries(symbolToCheck, true, reverse);
        let posY: number = symbolToCheck.position.y + ((reverse ? 1 : -1) * ( symbolToCheck.symbolHeight*0.5 ));
        // Check if it is visible by Restriction and symbol size.
        const nextSymName: string = this.generateNextSymbol();
        if (willSpawn) {
            
            const posID: number = reverse ? Math.max(...symbolToCheck.posID) + 1 : Math.min(...symbolToCheck.posID) - 1;

            let symState: string = ReelSymbolStates.NORMAL;
            if (this.state.isSpinning || this.state.isStarting || this.state.isStopping) symState = ReelSymbolStates.SPIN;

            const symbol: ReelBaseSymbol = this.addingNextStepSymbol(posID, nextSymName, symState);
            posY += (reverse ? symbol.symbolHeight : -symbol.symbolHeight)*0.5;
            symbol.position.y = posY;
            this.addSymbol(symbol);
        }

        return willSpawn;
    }

    protected generateNextSymbol(): string {
        return this.genSym.next().value;;
    }

    private *getNextSym(): Generator<string, string, string> {
        let i: number = 0;
        while (true) {
            yield this._currentReelSet[i % this._currentReelSet.length]
            i++;
        }
    }
}
